<template>
  <Layout title="Driver Management" description="Manage your drivers and their availability">
    <!-- Tabs -->
    <div class="overflow-x-auto">
      <div class="d-flex gap-2 mb-2 flex-nowrap" style="min-width: max-content;">
        <button
          class="btn px-3 py-1 rounded-pill fw-semibold"
          :class="tab === 'Driver' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
          @click="tab = 'Driver'"
        >
          Drivers
        </button>
        <button
          class="btn px-3 py-1 rounded-pill fw-semibold"
          :class="tab === 'Reports' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
          @click="switchToReports()"
        >
          Reports
        </button>
        <button
          class="btn px-3 py-1 rounded-pill fw-semibold"
          :class="tab === 'VoucherReports' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
          @click="switchToVoucherReports()"
        >
          Voucher Reports
        </button>
        <button
          class="btn px-3 py-1 rounded-pill fw-semibold"
          :class="tab === 'DownloadVoucher' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
          @click="tab = 'DownloadVoucher'"
        >
          Download Voucher
        </button>
      </div>
    </div>

    <!-- =========================
         DRIVER TAB
    ========================== -->
    <div v-if="tab === 'Driver'" class="space-y-6 mt-0">
      <div class="d-flex align-items-center justify-content-between">
        <h2 class="text-2xl fw-semibold">Drivers</h2>
        <div class="d-flex gap-2">
          <select class="form-select form-select-sm" style="width:auto" v-model="driverFilters.status" @change="fetchDrivers()">
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Assigned">Assigned</option>
            <option value="Inactive">Inactive</option>
          </select>
          <button class="btn px-3 py-1 rounded-md bg-primary text-white d-flex align-items-center" @click="isDriverDialogOpen = true">
            <Plus class="h-4 w-4 me-2" /> Add Driver
          </button>
        </div>
      </div>

      <!-- Driver Cards -->
      <div class="row g-3">
        <div class="col-12 col-md-6 col-lg-4" v-for="d in drivers" :key="d.id">
          <div class="border rounded-4 shadow-sm p-3 h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h5 class="mb-1 fw-bold">{{ d.name || 'Unnamed' }}</h5>
                <span class="badge rounded-pill me-2" :class="d.status === 'Active' ? 'bg-success' : 'bg-secondary'">
                  {{ d.status || 'Unknown' }}
                </span>
                <span class="badge rounded-pill bg-light text-dark border">
                  {{ d.vehicleType || 'N/A' }}
                </span>
              </div>
            </div>

            <div class="mt-2 small">
              <div><strong>Contact:</strong> {{ d.contactNumber }}</div>
              <div><strong>Email:</strong> {{ d.email }}</div>
              <div><strong>Vehicle Type:</strong> {{ d.vehicleType || '-' }}</div>
              <div><strong>Pax:</strong> {{ d.pax || '-' }}</div>
              <div><strong>Luggage Space:</strong> {{ d.luggageSpace || '-' }}</div>
              <div><strong>Vehicle No:</strong> {{ d.vehicleNo || '-' }}</div>
            </div>

            <div class="d-flex gap-2 pt-2 border-top mt-3">
              <button class="flex-fill btn btn-sm btn-outline-primary" @click="openDriverEditDialog(d)">Edit</button>
              <button class="flex-fill btn btn-sm btn-outline-danger" @click="deleteDriver(d.id)">Delete</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Driver Modal -->
      <div v-if="isDriverDialogOpen" class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <form @submit.prevent="createDriver">
              <div class="modal-header">
                <h5 class="modal-title">Add New Driver</h5>
                <button type="button" class="btn-close" @click="isDriverDialogOpen = false"></button>
              </div>

              <div class="modal-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Name</label>
                    <input v-model="driverForm.name" type="text" class="form-control" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Email</label>
                    <input v-model="driverForm.email" type="email" class="form-control" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Password</label>
                    <input v-model="driverForm.password" type="password" class="form-control" required minlength="6" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Contact Number</label>
                    <vue-tel-input
                      ref="telInputRef"
                      v-model="driverForm.contactNumber"
                      mode="international"
                      default-country="SG"
                      placeholder="e.g. +65 8123 4567"
                      required
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Vehicle Type</label>
                    <input v-model="driverForm.vehicleType" type="text" class="form-control" placeholder="e.g. Alphard" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Pax</label>
                    <input v-model="driverForm.pax" type="number" class="form-control" placeholder="e.g. 6" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Luggage Space</label>
                    <input v-model="driverForm.luggageSpace" type="number" class="form-control" placeholder="e.g. 3" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Start Location</label>
                    <input v-model="driverForm.startLocation" type="text" class="form-control" placeholder="e.g. Ubi" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Vehicle No</label>
                    <input v-model="driverForm.vehicleNo" type="text" class="form-control" placeholder="e.g. SJA1234X" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Status</label>
                    <select v-model="driverForm.status" class="form-select">
                      <option value="Active">Active</option>
                      <option value="Assigned">Assigned</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-success d-flex align-items-center gap-2" type="submit" :disabled="submittingDriver">
                  <Plus class="h-4 w-4" />
                  <span v-if="!submittingDriver">Add Driver</span>
                  <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Edit Driver Modal -->
      <div v-if="isDriverEditDialogOpen" class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <form @submit.prevent="updateDriver">
              <div class="modal-header">
                <h5 class="modal-title">Edit Driver</h5>
                <button type="button" class="btn-close" @click="isDriverEditDialogOpen = false"></button>
              </div>

              <div class="modal-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Name</label>
                    <input v-model="editDriverForm.name" type="text" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Email</label>
                    <input v-model="editDriverForm.email" type="email" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Contact Number</label>
                    <vue-tel-input
                      ref="telEditInputRef"
                      v-model="editDriverForm.contactNumber"
                      mode="international"
                      default-country="SG"
                      placeholder="e.g. +65 8123 4567"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Vehicle Type</label>
                    <input v-model="editDriverForm.vehicleType" type="text" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Pax</label>
                    <input v-model="editDriverForm.pax" type="number" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Luggage Space</label>
                    <input v-model="editDriverForm.luggageSpace" type="number" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Start Location</label>
                    <input v-model="editDriverForm.startLocation" type="text" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Vehicle No</label>
                    <input v-model="editDriverForm.vehicleNo" type="text" class="form-control" />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Status</label>
                    <select v-model="editDriverForm.status" class="form-select">
                      <option value="Active">Active</option>
                      <option value="Assigned">Assigned</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-primary d-flex align-items-center gap-2" type="submit" :disabled="submittingDriver">
                  <i class="bi bi-save"></i>
                  <span v-if="!submittingDriver">Save Changes</span>
                  <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <!-- =========================
         REPORTS TAB
    ========================== -->
    <div v-else-if="tab === 'Reports'" class="mt-3">
      <div class="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label class="form-label small mb-1">Start Date</label>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="reportFilters.startDate"
          />
        </div>

        <div>
          <label class="form-label small mb-1">End Date</label>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="reportFilters.endDate"
          />
        </div>

        <button
          class="btn btn-primary btn-sm mt-4"
          @click="fetchDriverKPIReports"
        >
          Generate
        </button>

        <button
          class="btn btn-outline-secondary btn-sm mt-4"
          @click="resetReportFilter"
        >
          Reset
        </button>
      </div>


      <!-- Loading -->
      <!-- <div v-if="loadingReports" class="text-center py-5 text-muted">
        <div class="spinner-border" role="status"></div>
        <p class="mt-3">Loading driver KPI reports...</p>
      </div> -->

      <!-- Empty -->
      <!-- <div v-else-if="!driverReports.length" class="alert alert-info text-center">
        No driver KPI reports found.
      </div> -->

      <!-- Reports Table -->
      <!-- <div v-else class="table-responsive shadow-sm rounded-3">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>#</th>
              <th>Report Period</th>
              <th>Generated At</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in driverReports" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ r.month || '-' }}</td>
              <td>{{ formatDate(r.generatedAt) }}</td>
              <td>
                <button 
                  v-if="r.filePath" 
                  class="btn btn-sm btn-primary"
                  @click="downloadDriverReport(r.id, r.filePath)"
                >
                  Download
                </button>
                <span v-else class="text-muted small">No file</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div> -->
    </div>
     <!-- =========================
         VOUCHER REPORTS TAB
    ========================== -->
    <div v-else-if="tab === 'VoucherReports'" class="mt-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2 class="fw-semibold mb-0">Voucher Reports</h2>
        <button
          class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          @click="fetchVoucherReports"
        >
          <i class="bi bi-arrow-clockwise"></i> Refresh
        </button>
      </div>

      <div v-if="loadingVoucherReports" class="text-center py-5 text-muted">
        <div class="spinner-border" role="status"></div>
        <p class="mt-3">Loading voucher reports...</p>
      </div> 

      <div v-else-if="!voucherReports.length" class="alert alert-info text-center">
        No voucher reports found.
      </div>

      <div v-else class="table-responsive shadow-sm rounded-3">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>#</th>
              <th>File Name</th>
              <th>Generated At</th>
               <th>Voucher Download</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in voucherReports" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ r.fileName || '-' }}</td>
              <td>{{ formatDate(r.generatedAt) }}</td>
              <td>
                
                  <button 
                    class="btn btn-sm btn-success"
                    :disabled="loading"
                    @click="downloadMergedVouchers(r.generatedAt)"
                  >
                    {{ loading ? 'Preparing...' : 'Download Weekly Merged PDF' }}
                  </button>
              </td>
              <td>
                <button
                  v-if="r.filePath"
                  class="btn btn-sm btn-primary"
                  @click="downloadVoucherReport(r.filePath)"
                >
                  Download
                </button>
                <span v-else class="text-muted small">No file</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- =========================
        DOWNLOAD VOUCHER TAB
    ========================== -->
    <div v-else-if="tab === 'DownloadVoucher'" class="mt-3">

      <!-- =====================
          DATE RANGE DOWNLOAD
      ====================== -->
      <div class="d-flex flex-wrap gap-2 align-items-end mb-3">

        <div>
          <label class="form-label small mb-1">Start Date</label>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="voucherDownloadFilters.startDate"
          />
        </div>

        <div>
          <label class="form-label small mb-1">End Date</label>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="voucherDownloadFilters.endDate"
          />
        </div>

        <button
          class="btn btn-success btn-sm mt-4"
          :disabled="downloadingVoucher"
          @click="downloadVoucherByDate"
        >
          <span v-if="!downloadingVoucher">Download PDF</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
        <button
          class="btn btn-success btn-sm mt-4"
          :disabled="downloadingVoucher"
          @click="downloadVoucherExcelByDate"
        >
          <span v-if="!downloadingVoucher">Download Excel</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>

      </div>

      <hr class="my-4"/>

      <h5 class="fw-semibold mb-3">Search Voucher</h5>

      <div class="d-flex gap-2 mb-3">
        <input
          type="text"
          class="form-control form-control-sm"
          v-model="searchVoucherId"
          placeholder="Enter Voucher ID"
        />
        <button
          class="btn btn-primary btn-sm"
          @click="searchVoucher"
        >
          Search
        </button>
      </div>

      <!-- Voucher List -->
      <div v-if="vouchers.length" class="space-y-4 mt-3">

        <div
          v-for="(v, idx) in vouchers"
          :key="v.docId || idx"
          class="border rounded shadow-sm bg-white p-3 mb-2"
        >
          <div>
            <h6 class="fw-semibold">
              Transport Voucher - {{ formatDate(v.createdAt) }}
            </h6>
            <div class="text-muted small">
              {{ v.docId }}
            </div>
          </div>

          <div class="mt-2 d-flex gap-2">

            <router-link
              :to="`/AdminVoucher/${v.docId}`"
              class="btn btn-sm btn-success"
            >
              View Voucher
            </router-link>

          </div>

        </div>

      </div>

      <div v-else-if="searchVoucherId && !vouchers.length" class="text-muted mt-2">
        No voucher found.
      </div>

      <hr class="my-4"/>

      <!-- =====================
          MANUAL VOUCHER IDS
      ====================== -->

      <h5 class="mb-3 fw-semibold">Merge By Voucher IDs</h5>

      <div v-for="(id, index) in voucherIDInputs" :key="index" class="d-flex gap-2 mb-2">
        <input
          type="text"
          class="form-control form-control-sm"
          v-model="voucherIDInputs[index]"
          placeholder="Enter Voucher ID"
        />
        <button
          class="btn btn-outline-danger btn-sm"
          @click="removeVoucherField(index)"
          v-if="voucherIDInputs.length > 1"
        >
          Remove
        </button>
      </div>

      <div class="d-flex gap-2 mt-2">
        <button
          class="btn btn-outline-primary btn-sm"
          @click="addVoucherField"
        >
          + Add Another
        </button>

        <button
          class="btn btn-primary btn-sm"
          :disabled="downloadingVoucher"
          @click="downloadVoucherByIDs"
        >
          <span v-if="!downloadingVoucher">Merge Selected</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
      </div>

    </div>
  </Layout>
</template>

<script>
import { Plus } from "lucide-vue-next";
import Layout from '@/components/Layout.vue';
import { useToast } from 'vue-toastification';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
import axios from "axios";
import {VueTelInput} from "vue3-tel-input";
import "vue3-tel-input/dist/vue3-tel-input.css";

export default {
  name: "DriverManagement",
  components: { Layout, Plus, VueTelInput },
  data() {
    return {
      tab: "Driver",
      reportFilters: {
        startDate: "",
        endDate: ""
      },
      voucherDownloadFilters: {
        startDate: "",
        endDate: ""
      },
      vouchers: [],   // 🔥 ADD THIS
      searchVoucherId: "",
      downloadingVoucher: false,
      voucherIDInputs: [""],
      drivers: [],
      isDriverDialogOpen: false,
      isDriverEditDialogOpen: false,
      submittingDriver: false,
      driverFilters: { status: "" },
      driverForm: {
        email: "",
        password: "",
        name: "",
        contactNumber: "",
        vehicleType: "",
        pax: "",
        luggageSpace: "",
        startLocation: "",
        vehicleNo: "", 
        status: "Active",
      },
      editDriverForm: {
        id: "",
        email: "",
        name: "",
        contactNumber: "",
        vehicleType: "",
        pax: "",
        luggageSpace: "",
        startLocation: "",
        vehicleNo: "", 
        status: "Active",
      },
      driverReports: [],   // ✅ ensure defined as an empty array
      loadingReports: false,
      voucherReports: [],   // ✅ make sure this exists
      loadingVoucherReports: false,
      loading:false
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  async mounted() {
    await this.checkAuthState();
    await this.checkPageAccess(["admin"])
    await this.fetchDrivers();
  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');
      const token = await user.getIdToken();
      return { Authorization: token };
    },
    async checkAuthState() {
      const auth = getAuth();
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const idToken = await user.getIdToken();
              this.token = idToken;
              resolve();
            } catch (error) {
              reject(error);
            }
          } else {
            router.push("/");
            reject(new Error("No user logged in"));
          }
        });
      });
    },
    resetReportFilter() {
      this.reportFilters.startDate = "";
      this.reportFilters.endDate = "";
      // this.fetchDriverKPIReports();
    },
    checkPageAccess(allowedRoles = []) {
      const access = this.$store.state.systemAccess || {}

      // check if any allowed role is true
      const hasAccess = allowedRoles.some(role => access[role] === true)

      if (!hasAccess) {
        this.toast.error("Access denied.")
        // 🔹 route them based on their actual role
        if (access.admin) {
          this.$router.push("/crm")
        } else if (access.client) {
          this.$router.push("/booking")
        } else if (access.driver) {
          this.$router.push("/jobManagement")
        } else {
          this.$router.push("/")
        }
      }
    },
    async downloadMergedVouchers(date) {
      try {
        this.loading = true;
        console.log(date)
        const rawDate = date || new Date();
        const isoDate = new Date(rawDate).toISOString().split("T")[0];
        const headers = await this.getAuthHeader();

        const res = await axios.get(
          this.$apiURL + "/api/vouchers/merge",
          {
            params: { date: isoDate },
            headers: headers,
            responseType: "blob" // VERY IMPORTANT — backend returns a file
          }
        );

        // convert blob
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // download file
        const link = document.createElement("a");
        link.href = url;
        link.download = `voucher_merge_${isoDate}_last7days.pdf`;
        link.click();

        window.URL.revokeObjectURL(url);

        this.toast.success("Merged voucher PDF downloaded!");
      } catch (e) {
        console.error(e);
        const msg = e.response?.data?.error || "Something wrong, cannot download.";
        this.toast.error(msg);
      } finally {
        this.loading = false;
      }
    },
    addVoucherField() {
      this.voucherIDInputs.push("");
    },

    removeVoucherField(index) {
      this.voucherIDInputs.splice(index, 1);
    },

    async downloadVoucherByIDs() {
      try {
        const cleanedIDs = this.voucherIDInputs
          .map(id => id.trim())
          .filter(id => id !== "");

        if (!cleanedIDs.length) {
          this.toast.error("Please enter at least one voucher ID");
          return;
        }

        this.downloadingVoucher = true;

        const headers = await this.getAuthHeader();

        const res = await axios.post(
          `${this.$apiURL}/api/vouchers/merge-by-ids`,
          { voucherIDs: cleanedIDs },
          {
            headers,
            responseType: "blob"
          }
        );

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `Selected_Vouchers_${new Date().toISOString().slice(0,10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);

        this.toast.success("Merged voucher PDF downloaded!");
      } catch (err) {
        console.error(err);
        this.toast.error(err.response?.data?.error || "Failed to merge vouchers");
      } finally {
        this.downloadingVoucher = false;
      }
    },
    async fetchDrivers() {
      try {
        const headers = await this.getAuthHeader();
        const params = {};
        if (this.driverFilters.status) params.status = this.driverFilters.status;
        const res = await axios.get(`${this.$apiURL}/drivers`, { headers, params });
        this.drivers = res.data.drivers || [];
      } catch (err) {
        this.toast.error("Failed to load drivers");
        this.drivers = [];
      }
    },
    async createDriver() {
      try {
        this.submittingDriver = true;
        const headers = await this.getAuthHeader();

        // 🔹 Safely extract phone number from vue-tel-input
        let rawPhoneNumber = this.$refs.telInputRef
          ? this.$refs.telInputRef.modelValue || this.$refs.telInputRef.number
          : this.driverForm.contactNumber;

        // 🔹 Fallback (some versions store it differently)
        if (!rawPhoneNumber && this.$refs.telInputRef) {
          rawPhoneNumber =
            this.$refs.telInputRef.phone ||
            this.$refs.telInputRef.modelValue ||
            this.driverForm.contactNumber;
        }

        const finalPhoneNumber = rawPhoneNumber || this.driverForm.contactNumber;

        // 🔹 Build payload
        const payload = {
          ...this.driverForm,
          contactNumber: finalPhoneNumber
        };

        const res = await axios.post(`${this.$apiURL}/drivers`, payload, { headers });

        if (res.status === 201) {
          this.toast.success("Driver created successfully");
          this.isDriverDialogOpen = false;

          // 🔹 Reset form
          this.driverForm = {
            email: "",
            password: "",
            name: "",
            contactNumber: "",
            vehicleType: "",
            pax: "",
            luggageSpace: "",
            startLocation: "",
            status: "Active",
          };

          await this.fetchDrivers();
        } else {
          this.toast.error(res.data?.error || "Failed to create driver");
        }
      } catch (err) {
        console.error("❌ Create driver error:", err);
        this.toast.error(err?.response?.data?.error || "Failed to create driver");
      } finally {
        this.submittingDriver = false;
      }
    },
    openDriverEditDialog(d) {
      this.editDriverForm = { ...d };
      this.isDriverEditDialogOpen = true;
    },
    async updateDriver() {
      try {
        this.submittingDriver = true;
        const headers = await this.getAuthHeader();

        // 🔹 Safely extract phone number from vue-tel-input (edit modal)
        let rawPhoneNumber = this.$refs.telEditInputRef
          ? this.$refs.telEditInputRef.modelValue || this.$refs.telEditInputRef.number
          : this.editDriverForm.contactNumber;

        // 🔹 Fallback (in case component stores it differently)
        if (!rawPhoneNumber && this.$refs.telEditInputRef) {
          rawPhoneNumber =
            this.$refs.telEditInputRef.phone ||
            this.$refs.telEditInputRef.modelValue ||
            this.editDriverForm.contactNumber;
        }

        const finalPhoneNumber = rawPhoneNumber || this.editDriverForm.contactNumber;

        // 🔹 Build payload (only include contactNumber if it exists)
        const { id, ...payload } = this.editDriverForm;
        if (finalPhoneNumber && finalPhoneNumber.trim() !== "") {
          payload.contactNumber = finalPhoneNumber;
        }

        const res = await axios.put(`${this.$apiURL}/drivers/${id}`, payload, { headers });

        if (res.data.ok) {
          this.toast.success("Driver updated successfully");
          this.isDriverEditDialogOpen = false;
          await this.fetchDrivers();
        } else {
          this.toast.error(res.data?.error || "Failed to update driver");
        }
      } catch (err) {
        console.error("❌ Update driver error:", err);
        this.toast.error(err?.response?.data?.error || "Failed to update driver");
      } finally {
        this.submittingDriver = false;
      }
    },
    async deleteDriver(id) {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.delete(`${this.$apiURL}/drivers/${id}`, { headers });
        if (res.data.ok) {
          this.toast.success("Driver deleted");
          await this.fetchDrivers();
        }
      } catch {
        this.toast.error("Failed to delete driver");
      }
    },
    /* ==========================
       REPORTS TAB METHODS
    =========================== */
    async switchToReports() {
      this.tab = "Reports";
      // if (!this.driverReports.length) await this.fetchDriverKPIReports();
    },
    async fetchDriverKPIReports() {
      try {
        this.loadingReports = true;

        if (!this.reportFilters.startDate || !this.reportFilters.endDate) {
          this.toast.error("Please select start date and end date");
          return;
        }

        const headers = await this.getAuthHeader();

        const payload = {
          startDate: this.reportFilters.startDate, // yyyy-mm-dd
          endDate: this.reportFilters.endDate
        };

        const res = await axios.post(
          `${this.$apiURL}/api/bookings/export/dec`,
          payload,
          {
            headers,
            responseType: "blob" // 🔴 IMPORTANT
          }
        );

        // 🔽 download excel
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Booking_Report_${payload.startDate}_to_${payload.endDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.toast.success("Report downloaded successfully");
      } catch (err) {
        console.error("❌ Export error:", err);
        this.toast.error(
          err.response.data.error || "Failed to generate report"
        );
      } finally {
        this.loadingReports = false;
      }
    },
    formatDate(dt) {
      if (!dt) return "-";
      return new Date(dt).toLocaleString("en-SG", { timeZone: "Asia/Singapore" });
    },
    async downloadDriverReport(id, filepath) {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(
          `${this.$apiURL}/api/report/${id}`, // 🔹 new route
          {
            headers,
            responseType: "blob", // ensure binary data
            params: { filePath: filepath }, // pass file path
          }
        );

        // ✅ Build Excel blob
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // ✅ Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filepath.split("/").pop() || "Driver_KPI_Report.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        console.log(`✅ Downloaded report: ${a.download}`);
      } catch (err) {
        console.error("❌ Error downloading report:", err);
        this.toast.error("Failed to download report. Please try again.");
      }
    },
     /* ==========================
       VOUCHER REPORT TAB
    =========================== */
    async switchToVoucherReports() {
      this.tab = "VoucherReports";
      if (!this.voucherReports.length) await this.fetchVoucherReports();
    },
    async fetchVoucherReports() {
      try {
        this.loadingVoucherReports = true;
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.$apiURL}/vouchers/reports/system_auto`, { headers });
        if (res.status === 200) {
          this.voucherReports = res.data.reports || [];
        } else {
          this.toast.error("Failed to load voucher reports");
          this.voucherReports = [];
        }
      } catch (err) {
        console.error("❌ Error fetching voucher reports:", err);
        this.toast.error("Failed to load voucher reports");
      } finally {
        this.loadingVoucherReports = false;
      }
    },
    async downloadVoucherReport(filePath) {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.$apiURL}/vouchers/report/download`, {
          headers,
          responseType: "blob",
          params: { filePath },
        });

        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filePath.split("/").pop() || "Voucher_Report.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("❌ Error downloading voucher report:", err);
        this.toast.error("Failed to download voucher report.");
      }
    },
    resetVoucherDownload() {
      this.voucherDownloadFilters.startDate = "";
      this.voucherDownloadFilters.endDate = "";
    },

    async downloadVoucherByDate() {
      try {
        if (!this.voucherDownloadFilters.startDate || !this.voucherDownloadFilters.endDate) {
          this.toast.error("Please select start and end date");
          return;
        }

        this.downloadingVoucher = true;

        const headers = await this.getAuthHeader();

        const res = await axios.get(
          `${this.$apiURL}/api/vouchers/merge-by-date`,
          {
            headers,
            responseType: "blob",
            params: {
              startDate: this.voucherDownloadFilters.startDate,
              endDate: this.voucherDownloadFilters.endDate
            }
          }
        );

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `Voucher_${this.voucherDownloadFilters.startDate}_to_${this.voucherDownloadFilters.endDate}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);

        this.toast.success("Voucher merged PDF downloaded!");
      } catch (err) {
        console.error(err);
        this.toast.error(err.response?.data?.error || "Failed to download vouchers");
      } finally {
        this.downloadingVoucher = false;
      }
    },
    async searchVoucher() {
      try {
        if (!this.searchVoucherId.trim()) {
          this.toast.error("Enter voucher ID");
          return;
        }

        const headers = await this.getAuthHeader();

        const res = await axios.get(
          `${this.$apiURL}/api/vouchers/search`,
          {
            headers,
            params: { voucherId: this.searchVoucherId.trim() }
          }
        );

        // 🔥 IMPORTANT: use vouchers array
        this.vouchers = res.data.vouchers || [];

      } catch (err) {
        console.error(err);
        this.vouchers = [];
        this.toast.error("Voucher not found");
      }
    },
    async downloadVoucherExcelByDate() {
        if (!this.voucherDownloadFilters.startDate || !this.voucherDownloadFilters.endDate) {
          this.toast.error("Please select start and end date");
          return;
        }

      try {
        this.downloadingVoucher = true;

        const headers = await this.getAuthHeader();

        const res = await axios.post(
          this.$apiURL+"/api/export/bookings-excel",
          {
            startDate: this.voucherDownloadFilters.startDate,
            endDate: this.voucherDownloadFilters.endDate
          },
          {
            headers,
            responseType: "blob" // VERY IMPORTANT
          }
        );

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Bookings_${this.voucherDownloadFilters.startDate}_to_${this.voucherDownloadFilters.endDate}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

      } catch (err) {
        console.error(err);
        this.toast.error("Download failed.");
      } finally {
        this.downloadingVoucher = false;
      }
    }

  },
};
</script>

<style scoped>
.w-85px { width: 85px !important; }
.w-250px { width: 250px !important; }

.hover-effect {
  transition: all 0.3s ease-in-out;
}
.hover-effect:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.1);
}
.table-responsive {
  overflow-x: auto;
}
@media (max-width: 768px) {
  table {
    font-size: 0.9rem;
  }
  th, td {
    white-space: nowrap;
  }
}
.modal {
  z-index: 9999 !important;
}

.modal-backdrop {
  z-index: 9999 !important;
}

.modal-content {
  position: relative;
  z-index: 9999;
}

</style>
